import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Paperclip, X, Send, Trash2, Pencil, Copy, Check, ExternalLink, RotateCcw, Ban, Landmark,
  Zap, RefreshCw, Mail, CheckCheck, Link2, MoreHorizontal,
} from 'lucide-react';
import PetanqueLoader from './PetanqueLoader';
import PaymentSheet from './PaymentSheet';
import usePollWhileActive from '../hooks/usePollWhileActive';
import MentionTextarea from './MentionTextarea';
import { useAuth } from '../contexts/AuthProvider';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog } from './ui/dialog';
import { Input } from './ui/input';
import { Select } from './ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { api } from '../api/sangly';
import { moneyExact, fmtDate, timeAgo, PROFORMA_STATUS, CURRENCIES } from '../lib/format';
import { cn } from '../lib/utils';

const ACCEPT = 'application/pdf,image/png,image/jpeg,image/gif,image/webp';
const MAX_BYTES = 10 * 1024 * 1024;
const SHOW_TIMELINE = true;   // the progress strip under the key facts
const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const initial = (name) => (name || '?').trim().charAt(0).toUpperCase() || '?';
const ticketNo = (url) => { const m = String(url || '').match(/\/(\d{5,})(?=[/?#]|$)/g); return m ? m[m.length - 1].replace(/\D/g, '') : ''; };
// the inline confirm strip for a status move
const ACTION = {
  paid: { label: 'Mark as paid', hint: 'How was it paid? (optional, goes in the log)' },
  finalized: { label: 'Mark as finalized', hint: 'How was the vendor told? (optional, goes in the log)' },
  cancelled: { label: 'Cancel this pro-forma', hint: 'Why? (optional, goes in the log)' },
  pending: { label: 'Reopen as to-pay', hint: 'Why? (optional, goes in the log)' },
};

// Render free text with any http(s) URL turned into a clickable link.
const URL_RE = /(https?:\/\/[^\s]+)/g;
function Linkify({ text }) {
  const s = String(text || '');
  if (!s) return null;
  return (
    <>
      {s.split(URL_RE).map((part, i) => (/^https?:\/\//.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noreferrer"
           className="break-all text-primary underline hover:no-underline">{part}</a>
      ) : part))}
    </>
  );
}

// Bytes come from an authenticated endpoint: fetch as a blob, open in a new tab.
const openFile = async (file) => {
  try {
    const blob = await api.proformas.fileBlob(file.id);
    window.open(URL.createObjectURL(blob), '_blank', 'noopener');
  } catch { /* ignore */ }
};

// --- small pieces from the handoff -------------------------------------------
const Dot = () => <span className="text-border">·</span>;

// quiet action at the right of a list row (icon or short text)
function Quiet({ onClick, title, children, className }) {
  return (
    <button type="button" onClick={onClick} title={title}
            className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] text-muted-foreground/80 hover:bg-muted hover:text-foreground disabled:opacity-50', className)}>
      {children}
    </button>
  );
}

// a "Files & links" row: icon tile, title/subtitle, quiet actions
function FileRow({ icon: Icon, title, subtitle, actions, onTitle, href }) {
  const titleEl = href
    ? <a href={href} target="_blank" rel="noreferrer" title={title} className="block max-w-full truncate text-[14px] font-medium text-primary hover:underline">{title}</a>
    : onTitle
      ? <button type="button" onClick={onTitle} title={title} className="block w-full max-w-full truncate text-left text-[14px] font-medium hover:underline">{title}</button>
      : <div title={title} className="truncate text-[14px] font-medium">{title}</div>;
  return (
    <div className="flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-muted/40">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[7px] bg-primary/12 text-primary"><Icon className="h-3.5 w-3.5" /></span>
      <div className="min-w-0 flex-1 overflow-hidden">
        {titleEl}
        {subtitle && <div className="truncate text-[12px] text-muted-foreground">{subtitle}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-0.5">{actions}</div>}
    </div>
  );
}

function Fact({ label, children, className }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted-foreground">{label}</div>
      <div className="mt-1 min-w-0">{children}</div>
    </div>
  );
}

// the progress strip: done steps are green dots joined by a line
function Timeline({ steps }) {
  return (
    <div className="mt-3.5 flex items-center">
      {steps.map((s, i) => (
        <div key={s.key} className={cn('flex items-center', i < steps.length - 1 && 'flex-1')}>
          <div className="flex items-center gap-2">
            <span className={cn('h-2 w-2 shrink-0 rounded-full', s.at ? 'bg-emerald-600' : 'bg-border')} />
            <span className="whitespace-nowrap text-[12px] text-muted-foreground">
              <span className={cn('font-semibold', s.at ? 'text-foreground/80' : 'text-muted-foreground')}>{s.label}</span>
              {s.at && <> {fmtDate(s.at).replace(/ \d{4}$/, '')}{s.who ? ` · ${s.who}` : ''}</>}
            </span>
          </div>
          {i < steps.length - 1 && <span className="mx-3 h-px flex-1 bg-border" />}
        </div>
      ))}
    </div>
  );
}

export default function ProformaDetailDialog({ proformaId, open, onClose, onChanged, users = [] }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!user?.is_app_admin;
  const [pf, setPf] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  // status move awaiting confirmation: 'paid' | 'cancelled' | 'pending' | null
  const [action, setAction] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [paySheet, setPaySheet] = useState(false);   // the payment sheet (payers only)
  // finalize: the email to the vendor (draft from the server, editable)
  const [mail, setMail] = useState(null);            // { to, subject, body, attachment } | null
  const [mailOpen, setMailOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [mailErr, setMailErr] = useState('');
  const [mailVia, setMailVia] = useState('email');   // email | gorgias
  const [fetchingLetter, setFetchingLetter] = useState(false);
  // Gorgias ticket URL - inline edit on the row
  const [editingGorgias, setEditingGorgias] = useState(false);
  const [gorgiasVal, setGorgiasVal] = useState('');
  const [savingGorgias, setSavingGorgias] = useState(false);
  const [letterErr, setLetterErr] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);   // the note composer expands on focus
  // details (amount / references / deadline) - inline edit
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [savingForm, setSavingForm] = useState(false);
  const [formErr, setFormErr] = useState('');
  // extra information - click to edit
  const [editingRemarks, setEditingRemarks] = useState(false);
  const [remarks, setRemarks] = useState('');
  // documents
  const docRef = useRef(null);
  const [docErr, setDocErr] = useState('');
  // composer
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState([]);
  const [files, setFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [attachErr, setAttachErr] = useState('');
  const fileRef = useRef(null);
  // editing a note
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editFiles, setEditFiles] = useState([]);
  const [editRemove, setEditRemove] = useState([]);
  const editFileRef = useRef(null);
  // The parent passes a fresh onClose on every render; keep it out of the
  // loader's deps or a soft refresh would wipe the dialog (and the draft note).
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const load = useCallback(async () => {
    if (!proformaId) return;
    setLoading(true);
    try {
      const [p, c, h] = await Promise.all([
        api.proformas.get(proformaId), api.proformas.comments(proformaId), api.proformas.history(proformaId),
      ]);
      setPf(p); setComments(c); setHistory(h);
    } catch { onCloseRef.current?.(); } finally { setLoading(false); }
  }, [proformaId]);

  useEffect(() => {
    if (!open) return;
    setPf(null); setComments([]); setHistory([]);
    setAction(null); setActionNote(''); setEditing(false); setFormErr(''); setEditingRemarks(false); setPaySheet(false);
    setMail(null); setMailOpen(false); setMailErr(''); setEditingGorgias(false);
    setBody(''); setMentions([]); setFiles([]); setAttachErr(''); setDocErr(''); setEditingId(null); setComposerOpen(false);
    load();
  }, [open, load]);

  // one stream: notes and status moves, oldest first - the newest sits right
  // above the composer at the bottom
  const activity = useMemo(() => [
    ...comments.map((c) => ({ key: `n${c.id}`, kind: 'note', at: c.created_at, c })),
    ...history.map((h) => ({ key: `s${h.id}`, kind: 'status', at: h.created_at, h })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at)), [comments, history]);

  // an Airwallex transfer in flight: re-read its status every 10s until final
  const [checking, setChecking] = useState(false);
  const checkTransfer = async () => {
    if (!pf?.airwallex_transfer_id) return;
    setChecking(true);
    try {
      const p = await api.proformas.checkTransfer(pf.id);
      setPf(p);
      if (!p.in_payment) { setHistory(await api.proformas.history(pf.id)); onChanged?.(); }
    } catch { /* ignore */ } finally { setChecking(false); }
  };
  usePollWhileActive(!!pf?.in_payment, checkTransfer, 10000);

  // soft refresh after a change: keep what's on screen, swap the data underneath
  const refresh = async () => {
    try {
      const [p, h] = await Promise.all([api.proformas.get(proformaId), api.proformas.history(proformaId)]);
      setPf(p); setHistory(h);
    } catch { /* ignore */ }
    onChanged?.();
  };

  if (!open) return null;
  // anyone can edit while it's pending (the payer often isn't who logged it);
  // archived ones are admin-only. Deleting stays with the creator or an admin.
  const canModify = !!pf && (isAdmin || pf.status === 'pending');
  const canDelete = !!pf && (isAdmin || (pf.added_by_id === user?.id && pf.status === 'pending'));

  const copyRef = () => {
    if (!pf?.payment_reference) return;
    navigator.clipboard?.writeText(pf.payment_reference).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const applyStatus = async () => {
    if (!pf || !action) return;
    setSavingStatus(true);
    try {
      await api.proformas.setStatus(pf.id, action, actionNote.trim());
      setAction(null); setActionNote('');
      await refresh();
    } finally { setSavingStatus(false); }
  };

  const openMail = async () => {
    setMailErr(''); setMailOpen(true);
    if (!mail) {
      try {
        const d = await api.proformas.notifyVendorDraft(pf.id);
        setMail(d);
        // default to the ticket when there is one - the vendor keeps one thread
        setMailVia(d.gorgias?.enabled && d.gorgias?.ticket_id && !d.gorgias?.error ? 'gorgias' : 'email');
      } catch (e) { setMailErr(e?.response?.data?.detail || 'Could not prepare the message.'); }
    }
  };
  const sendMail = async (finalize) => {
    if (!mail?.to?.trim()) { setMailErr('A vendor email address is needed.'); return; }
    setSending(true); setMailErr('');
    try {
      const p = await api.proformas.notifyVendor(pf.id, { to: mail.to.trim(), subject: mail.subject, body: mail.body, finalize, via: mailVia });
      setPf(p); setMailOpen(false); setHistory(await api.proformas.history(pf.id)); onChanged?.();
    } catch (e) { setMailErr(e?.response?.data?.detail || 'Could not send.'); }
    finally { setSending(false); }
  };
  const fetchLetter = async () => {
    setFetchingLetter(true); setLetterErr('');
    try { setPf(await api.proformas.confirmationLetter(pf.id)); onChanged?.(); }
    catch (e) { setLetterErr(e?.response?.data?.detail || `Could not fetch the letter (${e?.response?.status || 'network'}).`); }
    finally { setFetchingLetter(false); }
  };

  const saveGorgias = async () => {
    setSavingGorgias(true);
    try {
      const p = await api.proformas.update(pf.id, { gorgias_url: gorgiasVal.trim() });
      setPf(p); setEditingGorgias(false); setMail(null); onChanged?.();
    } catch { /* keep editing */ } finally { setSavingGorgias(false); }
  };

  const startEditDetails = () => {
    setForm({ amount: Number(pf.amount) > 0 ? pf.amount : '', currency: pf.currency || 'EUR',
              their_reference: pf.their_reference || '', our_reference: pf.our_reference || '',
              due_date: pf.due_date || '', gorgias_url: pf.gorgias_url || '' });
    setFormErr(''); setEditing(true);
  };
  const saveDetails = async () => {
    if (!(Number(form.amount) > 0)) { setFormErr('Enter an amount greater than 0.'); return; }
    setSavingForm(true); setFormErr('');
    try {
      const p = await api.proformas.update(pf.id, { ...form, due_date: form.due_date || null });
      setPf(p); setEditing(false); onChanged?.();
    } catch (e) {
      const d = e?.response?.data;
      setFormErr(d?.detail || (d && Object.values(d).flat().join(' ')) || 'Could not save.');
    } finally { setSavingForm(false); }
  };
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveRemarks = async () => {
    if (remarks === (pf.remarks || '')) { setEditingRemarks(false); return; }
    try {
      const p = await api.proformas.update(pf.id, { remarks });
      setPf(p); setEditingRemarks(false); onChanged?.();
    } catch { /* keep editing */ }
  };

  const validFiles = (list, setErr) => {
    const bad = list.find((f) => f.size > MAX_BYTES);
    if (bad) { setErr(`"${bad.name}" is too large - the limit is 10 MB per file.`); return null; }
    setErr(''); return list;
  };
  const addDocuments = async (list) => {
    const ok = validFiles(list, setDocErr);
    if (docRef.current) docRef.current.value = '';
    if (!ok || !ok.length) return;
    try {
      const p = await api.proformas.update(pf.id, {}, { files: ok });
      setPf(p); onChanged?.();
    } catch (e) { setDocErr(e?.response?.data?.detail || 'Could not upload.'); }
  };
  const removeDocument = async (d) => {
    if (!window.confirm(`Remove "${d.name}"?`)) return;
    try {
      await api.proformas.deleteFile(d.id);
      setPf((p) => ({ ...p, documents: p.documents.filter((x) => x.id !== d.id) }));
      onChanged?.();
    } catch { /* ignore */ }
  };

  const postComment = async () => {
    if (!body.trim() && files.length === 0) return;
    setPosting(true);
    try {
      const c = await api.proformas.addComment(pf.id, body.trim(), mentions, files);
      setComments((prev) => [...prev, c]);
      setBody(''); setMentions([]); setFiles([]); setAttachErr('');
      setPf((p) => ({ ...p, comment_count: (p.comment_count || 0) + 1 }));
      onChanged?.();
    } catch (e) { setAttachErr(e?.response?.data?.detail || 'Could not post.'); }
    finally { setPosting(false); }
  };
  const startEdit = (c) => { setEditingId(c.id); setEditBody(c.body || ''); setEditFiles([]); setEditRemove([]); };
  const cancelEdit = () => { setEditingId(null); setEditBody(''); setEditFiles([]); setEditRemove([]); };
  const saveEdit = async (c) => {
    try {
      const updated = await api.proformas.editComment(c.id, { body: editBody.trim(), files: editFiles, remove: editRemove });
      setComments((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...updated } : x)));
    } catch { /* ignore */ }
    cancelEdit();
  };
  const removeNote = async (c) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    setComments((prev) => prev.filter((x) => x.id !== c.id));
    try { await api.proformas.deleteComment(c.id); onChanged?.(); } catch { load(); }
  };
  const removeProforma = async () => {
    if (!window.confirm(`Delete the pro-forma from ${pf.supplier_name}? This can't be undone.`)) return;
    try { await api.proformas.remove(pf.id); onChanged?.(); onClose?.(); } catch { /* ignore */ }
  };

  const st = PROFORMA_STATUS[pf?.status] || PROFORMA_STATUS.pending;
  const late = pf?.status === 'pending' && pf?.due_date && pf.due_date < new Date().toISOString().slice(0, 10);
  const fillIn = canModify
    ? <button type="button" onClick={startEditDetails} className="text-[13px] font-medium text-amber-600 hover:underline">Fill in</button>
    : <span className="text-muted-foreground">-</span>;
  const labelCls = 'mb-1 block text-[12px] font-medium';

  // the payment cell: Airwallex reference + status, or how it was / will be paid
  // (pf is null while loading - everything derived here must tolerate that)
  const awDone = ['SENT', 'PAID', 'COMPLETED'].includes(pf?.airwallex_status);
  const awBad = ['FAILED', 'CANCELLED', 'SUSPENDED', 'REJECTED'].includes(pf?.airwallex_status);
  const steps = !pf ? [] : pf.status === 'cancelled'
    ? [{ key: 'logged', label: 'Logged', at: pf.added_date, who: pf.added_by_name },
       { key: 'cancelled', label: 'Cancelled', at: pf.updated_at, who: '' }]
    : [{ key: 'logged', label: 'Logged', at: pf.added_date, who: pf.added_by_name },
       { key: 'paid', label: 'Paid', at: pf.paid_at, who: pf.paid_by_name },
       { key: 'told', label: 'Vendor told', at: pf.vendor_notified_at, who: pf.vendor_notified_via === 'gorgias' ? 'Gorgias' : pf.vendor_notified_at ? 'email' : '' },
       { key: 'final', label: 'Finalized', at: pf.finalized_at, who: pf.finalized_by_name }];
  const documents = (pf?.documents || []).filter((d) => d.id !== pf?.airwallex_letter?.id);
  const composerShown = composerOpen || !!body.trim() || files.length > 0 || mentions.length > 0;
  const iconBtn = 'inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-[13px] font-medium hover:bg-muted';

  return (
    <Dialog open={open} onClose={onClose} className="max-w-[720px] rounded-2xl p-0">
      {loading || !pf ? (
        <div className="grid h-64 place-items-center"><PetanqueLoader size={38} roll /></div>
      ) : (
        <div className="max-h-[calc(100dvh-1rem)] overflow-y-auto overflow-x-hidden sm:max-h-[88vh]">
          {/* ------------------------------------------------ 1. header */}
          <div className="px-5 pb-5 pt-6 sm:px-7">
            <div className="flex flex-wrap items-start justify-between gap-x-3.5 gap-y-2.5">
              <div className="min-w-0 flex-1 basis-[300px]">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                  {pf.supplier
                    ? <button type="button" onClick={() => navigate(`/suppliers/${pf.supplier}`)} title="Open the vendor"
                              className="min-w-0 max-w-full break-words text-left text-[20px] font-semibold leading-tight tracking-[-0.01em] hover:underline">{pf.supplier_name}</button>
                    : <span className="min-w-0 max-w-full break-words text-[20px] font-semibold leading-tight tracking-[-0.01em]">{pf.supplier_name || 'Pro-forma'}</span>}
                  <Badge variant={st.variant} className="shrink-0 rounded-full px-2.5 text-[12px] font-semibold">{st.label}</Badge>
                  {pf.in_payment && <Badge variant="info" className="shrink-0 rounded-full"><Zap className="h-3 w-3" /> {pf.airwallex_status}</Badge>}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[13px] text-muted-foreground">
                  <span>Pro-forma</span>
                  {(pf.their_reference || pf.our_reference) && (
                    <>
                      <Dot />
                      <span className="font-mono text-foreground/80">{pf.their_reference || pf.our_reference}</span>
                      <button type="button" onClick={copyRef} title="Copy the payment communication" className="text-muted-foreground/80 hover:text-foreground">
                        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    </>
                  )}
                  {pf.their_reference && pf.our_reference && <><Dot /><span>PO <span className="font-mono text-foreground/80">{pf.our_reference}</span></span></>}
                  {!pf.their_reference && !pf.our_reference && canModify && <><Dot /><button type="button" onClick={startEditDetails} className="text-amber-600 hover:underline">add the references</button></>}
                  {!pf.supplier && <><Dot /><span>not linked to a vendor</span></>}
                </div>
              </div>

              {/* actions: the one or two that matter for this status, Edit, and a menu for the rest */}
              <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2 pr-7">
                {pf.in_payment ? (
                  <button type="button" onClick={checkTransfer} disabled={checking} className={iconBtn}>
                    <RefreshCw className={cn('h-3.5 w-3.5', checking && 'animate-spin')} /> Refresh
                  </button>
                ) : pf.status === 'pending' ? (
                  <>
                    {user?.can_pay && (
                      <Button size="sm" onClick={() => setPaySheet(true)} title="Open the payment sheet"><Landmark className="h-4 w-4" /> Pay</Button>
                    )}
                    <button type="button" onClick={() => setAction('paid')} className={iconBtn}><Check className="h-3.5 w-3.5" /> Mark as paid</button>
                  </>
                ) : pf.status === 'paid' ? (
                  <>
                    <Button size="sm" onClick={openMail} title="Send the vendor a payment confirmation (with the payment proof) and finalize"><Mail className="h-4 w-4" /> Email the vendor</Button>
                    <button type="button" onClick={() => setAction('finalized')} className={iconBtn}><CheckCheck className="h-3.5 w-3.5" /> Mark as finalized</button>
                  </>
                ) : (
                  <button type="button" onClick={() => setAction(pf.status === 'finalized' ? 'paid' : 'pending')} className={iconBtn}
                          title={pf.status === 'finalized' ? 'Back to "to finalize"' : 'Reopen as to-pay'}>
                    <RotateCcw className="h-3.5 w-3.5" /> Reopen
                  </button>
                )}
                {canModify && !editing && (
                  <button type="button" onClick={startEditDetails} className={iconBtn}><Pencil className="h-3.5 w-3.5" /> Edit</button>
                )}
                {((pf.status === 'pending' && !pf.in_payment) || pf.status === 'paid' || canDelete) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" title="More" className="grid h-8 w-8 place-items-center rounded-lg border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {pf.status === 'pending' && !pf.in_payment && (
                        <DropdownMenuItem onSelect={() => setAction('cancelled')} className="gap-2"><Ban className="h-4 w-4" /> Cancel pro-forma</DropdownMenuItem>
                      )}
                      {pf.status === 'paid' && (
                        <DropdownMenuItem onSelect={() => setAction('pending')} className="gap-2"><RotateCcw className="h-4 w-4" /> Not paid after all</DropdownMenuItem>
                      )}
                      {canDelete && (
                        <>
                          {(pf.status === 'pending' || pf.status === 'paid') && <DropdownMenuSeparator />}
                          <DropdownMenuItem onSelect={removeProforma} className="gap-2 text-rose-600 focus:text-rose-600"><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            {/* key facts - or the edit form in their place */}
            {editing ? (
              <div className="mt-3.5 rounded-xl bg-muted/40 px-[18px] py-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div>
                    <label className={labelCls}>Amount <span className="text-rose-500">*</span></label>
                    <Input type="number" min="0" step="0.01" value={form.amount} onChange={setF('amount')} placeholder="0.00" className="h-9" autoFocus />
                  </div>
                  <div>
                    <label className={labelCls}>Currency</label>
                    <Select value={form.currency} onChange={setF('currency')} className="h-9 w-full">
                      {CURRENCIES.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
                    </Select>
                  </div>
                  <div>
                    <label className={labelCls}>Deadline</label>
                    <Input type="date" value={form.due_date} onChange={setF('due_date')} className="h-9" />
                  </div>
                  <div>
                    <label className={labelCls}>Their order no.</label>
                    <Input value={form.their_reference} onChange={setF('their_reference')} placeholder="On their pro-forma" className="h-9 font-mono" />
                  </div>
                  <div>
                    <label className={labelCls}>Our PO no.</label>
                    <Input value={form.our_reference} onChange={setF('our_reference')} placeholder="PO00921" className="h-9 font-mono" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className={labelCls}>Gorgias ticket</label>
                    <Input value={form.gorgias_url} onChange={setF('gorgias_url')} placeholder="Ticket URL" className="h-9" />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <Button size="sm" onClick={saveDetails} disabled={savingForm}>
                    {savingForm ? <PetanqueLoader size={16} /> : <Check className="h-4 w-4" />} Save
                  </Button>
                  <button onClick={() => setEditing(false)} className="text-[13px] text-muted-foreground hover:underline">Cancel</button>
                  {formErr && <span className="text-[12px] text-rose-600">{formErr}</span>}
                </div>
              </div>
            ) : (
              <div className="mt-3.5 grid grid-cols-2 gap-3 rounded-xl bg-muted/40 px-[18px] py-4 sm:grid-cols-[1fr_1fr_2fr]">
                <Fact label="Amount">
                  {Number(pf.amount) > 0
                    ? <span className="text-[22px] font-semibold leading-tight tnum">{moneyExact(pf.amount, pf.currency)}</span>
                    : fillIn}
                </Fact>
                <Fact label="Deadline">
                  {pf.due_date
                    ? <span className={cn('text-[16px] font-medium leading-tight', late && 'text-rose-600')}>{fmtDate(pf.due_date)}{late && <span className="ml-1.5 text-[11px] font-semibold">late</span>}</span>
                    : fillIn}
                </Fact>
                <Fact label="Payment" className="col-span-2 sm:col-span-1">
                  {pf.airwallex_transfer_id ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Zap className="h-4 w-4 shrink-0 text-primary" />
                      <span className="whitespace-nowrap font-mono text-[14px]">{pf.airwallex_short_reference || pf.airwallex_transfer_id}</span>
                      <Badge variant={awBad ? 'destructive' : awDone ? 'success' : 'info'} className="rounded-full px-2 py-0 text-[11px] font-semibold">{pf.airwallex_status || '…'}</Badge>
                      {pf.airwallex_source_currency && <span className="text-[12px] text-muted-foreground">{pf.airwallex_source_currency}</span>}
                      {pf.airwallex_data?.fee_amount > 0 && <span className="text-[12px] text-muted-foreground">· fee {moneyExact(pf.airwallex_data.fee_amount, pf.airwallex_data.fee_currency || pf.currency)}</span>}
                    </div>
                  ) : pf.paid_at ? (
                    <div className="text-[14px] leading-tight">Manual transfer<span className="text-[12px] text-muted-foreground"> · {fmtDate(pf.paid_at)}{pf.paid_by_name ? ` by ${pf.paid_by_name}` : ''}</span></div>
                  ) : (
                    <div className="text-[14px] leading-tight text-muted-foreground">{pf.status === 'cancelled' ? 'Cancelled' : 'Not paid yet'}</div>
                  )}
                  {pf.airwallex_external_reference && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">bank ref <span className="font-mono">{pf.airwallex_external_reference}</span></div>
                  )}
                </Fact>
              </div>
            )}

            {SHOW_TIMELINE && !editing && <Timeline steps={steps} />}
          </div>

          {/* status confirm strip */}
          {action && (
            <div className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/40 px-5 py-3.5 sm:px-7">
              <span className="text-[13px] font-medium">{ACTION[action].label}</span>
              <Input value={actionNote} onChange={(e) => setActionNote(e.target.value)} autoFocus
                     onKeyDown={(e) => { if (e.key === 'Enter') applyStatus(); }}
                     placeholder={ACTION[action].hint} className="h-8 min-w-[200px] flex-1 text-[12px]" />
              <Button size="sm" onClick={applyStatus} disabled={savingStatus}>
                {savingStatus ? <PetanqueLoader size={16} /> : <Check className="h-4 w-4" />} Confirm
              </Button>
              <button onClick={() => { setAction(null); setActionNote(''); }} className="text-[12px] text-muted-foreground hover:underline">Cancel</button>
            </div>
          )}

          {/* tell the vendor */}
          {mailOpen && (
            <div className="border-t border-sky-200 bg-sky-500/[0.06] px-5 py-4 sm:px-7">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-[13px] font-medium">
                <Mail className="h-4 w-4 text-sky-600" /> Tell the vendor it&apos;s paid
                {mail && (
                  <span className="ml-auto inline-flex rounded-md border border-border bg-card p-0.5 text-[12px] font-medium">
                    {[['email', 'By email'], ['gorgias', 'On the Gorgias ticket']].map(([k, label]) => {
                      const off = k === 'gorgias' && !(mail.gorgias?.enabled && mail.gorgias?.ticket_id);
                      return (
                        <button key={k} type="button" disabled={off} onClick={() => setMailVia(k)}
                                title={off ? (mail.gorgias?.enabled ? 'Add the Gorgias ticket URL to the pro-forma first' : 'Gorgias is not connected') : ''}
                                className={cn('rounded px-2 py-0.5', mailVia === k ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:text-foreground', off && 'opacity-40')}>
                          {label}
                        </button>
                      );
                    })}
                  </span>
                )}
              </div>
              {!mail && !mailErr ? <div className="py-2"><PetanqueLoader size={20} /></div> : mail && (
                <div className="space-y-2">
                  {mail.gorgias && !(mail.gorgias.enabled && mail.gorgias.ticket_id) && (
                    <div className="text-[12px] text-amber-700">
                      {!mail.gorgias.enabled
                        ? 'Gorgias is not connected on the server (GORGIAS_DOMAIN / GORGIAS_AUTH not set), so only email is available.'
                        : 'No Gorgias ticket on this pro-forma - add the ticket under Files & links to reply on it.'}
                    </div>
                  )}
                  {mailVia === 'gorgias' && (
                    <div className="text-[12px] text-muted-foreground">
                      Reply on <a href={mail.gorgias.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">ticket #{mail.gorgias.ticket_id}</a>
                      {mail.gorgias.subject && <> · &ldquo;{mail.gorgias.subject}&rdquo;</>}
                      {mail.gorgias.mailbox && <> · sent from {mail.gorgias.mailbox}</>}
                      {mail.gorgias.agent && <> as {mail.gorgias.agent}</>}
                      {mail.gorgias.error && <span className="text-rose-600"> · {mail.gorgias.error}</span>}
                    </div>
                  )}
                  <div className="grid grid-cols-[70px_1fr] items-center gap-2 text-[12px]">
                    <span className="text-muted-foreground">To</span>
                    <Input value={mail.to} onChange={(e) => setMail((m) => ({ ...m, to: e.target.value }))} placeholder="vendor@example.com" className={cn('h-8 text-[12px]', !mail.to && 'border-amber-400')} />
                    <span className="text-muted-foreground">Subject</span>
                    <Input value={mail.subject} onChange={(e) => setMail((m) => ({ ...m, subject: e.target.value }))} className="h-8 text-[12px]" />
                  </div>
                  <textarea value={mail.body} onChange={(e) => setMail((m) => ({ ...m, body: e.target.value }))} rows={7}
                            className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-[13px] leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary" />
                  <div className="flex flex-wrap items-center gap-3 text-[12px] text-muted-foreground">
                    {mail.attachment
                      ? <span className="inline-flex items-center gap-1 text-emerald-700"><FileText className="h-3.5 w-3.5" /> {mail.attachment} attached</span>
                      : pf.airwallex_transfer_id
                        ? <button type="button" onClick={fetchLetter} disabled={fetchingLetter} className="inline-flex items-center gap-1 text-amber-700 hover:underline"><RefreshCw className={cn('h-3.5 w-3.5', fetchingLetter && 'animate-spin')} /> no payment proof yet - fetch it</button>
                        : <span>no payment proof (manual transfer)</span>}
                    {mailVia === 'email' && mail.reply_to && <span>· replies go to {mail.reply_to}, you&apos;re in cc</span>}
                    {mailVia === 'gorgias' && <span>· replies land on the ticket</span>}
                  </div>
                  {mailErr && <div className="text-[12px] text-rose-600">{mailErr}</div>}
                  <div className="flex flex-wrap items-center gap-3">
                    <Button size="sm" onClick={() => sendMail(true)} disabled={sending}>
                      {sending ? <PetanqueLoader size={16} /> : <Send className="h-4 w-4" />} Send & finalize
                    </Button>
                    <button type="button" onClick={() => sendMail(false)} disabled={sending} className="text-[12px] text-muted-foreground hover:underline">Send, keep in &quot;to finalize&quot;</button>
                    <button type="button" onClick={() => setMailOpen(false)} className="ml-auto text-[12px] text-muted-foreground hover:underline">Cancel</button>
                  </div>
                </div>
              )}
              {mailErr && !mail && <div className="text-[12px] text-rose-600">{mailErr}</div>}
            </div>
          )}

          {/* ------------------------------------------------ 2. files & links */}
          <div className="border-t border-border px-5 py-5 sm:px-7">
            <div className="mb-2.5 flex items-center justify-between gap-3 px-2.5">
              <h3 className="text-[13px] font-semibold text-foreground/80">Files &amp; links</h3>
              {canModify && (
                <>
                  <input ref={docRef} type="file" hidden multiple accept={ACCEPT} onChange={(e) => addDocuments(Array.from(e.target.files || []))} />
                  <button type="button" onClick={() => docRef.current?.click()} className="text-[12px] font-medium text-primary hover:underline">+ Add file</button>
                </>
              )}
            </div>
            <div className="space-y-0.5">
              {documents.map((d) => (
                <FileRow key={d.id} icon={FileText} title={d.name || 'file'} onTitle={() => openFile(d)}
                         subtitle={d.size > 0 ? fmtSize(d.size) : undefined}
                         actions={<>
                           <Quiet onClick={() => openFile(d)} title="Open"><ExternalLink className="h-3.5 w-3.5" /></Quiet>
                           {canModify && <Quiet onClick={() => removeDocument(d)} title="Remove"><X className="h-3.5 w-3.5" /></Quiet>}
                         </>} />
              ))}
              {documents.length === 0 && canModify && !pf.airwallex_transfer_id && (
                <button type="button" onClick={() => docRef.current?.click()}
                        className="flex w-full items-center gap-3 rounded-lg border border-dashed border-amber-300 px-2.5 py-2 text-left text-[13px] text-amber-700 hover:bg-amber-500/5">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[7px] bg-amber-500/10"><Paperclip className="h-3.5 w-3.5" /></span>
                  Attach the pro-forma PDF the vendor sent
                </button>
              )}
              {pf.airwallex_transfer_id && (
                <FileRow icon={Zap} title="Transfer confirmation"
                         onTitle={pf.airwallex_letter ? () => openFile(pf.airwallex_letter) : undefined}
                         subtitle={pf.airwallex_letter
                           ? `Airwallex · ${pf.airwallex_letter.name}${pf.airwallex_data?.dispatch_date ? ` · dispatched ${fmtDate(pf.airwallex_data.dispatch_date)}` : ''}`
                           : letterErr || 'Airwallex · payment proof not fetched yet'}
                         actions={<>
                           <Quiet onClick={fetchLetter} title={pf.airwallex_letter ? 'Fetch it again from Airwallex' : 'Fetch the payment proof (PDF) from Airwallex'} className="px-2">
                             {fetchingLetter ? <PetanqueLoader size={12} /> : null}{pf.airwallex_letter ? 'Refresh' : 'Get'}
                           </Quiet>
                           {pf.airwallex_letter && <Quiet onClick={() => openFile(pf.airwallex_letter)} title="Open"><ExternalLink className="h-3.5 w-3.5" /></Quiet>}
                         </>} />
              )}
              {editingGorgias ? (
                <div className="flex flex-wrap items-center gap-2 rounded-lg px-2.5 py-2">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[7px] bg-primary/12 text-primary"><Link2 className="h-3.5 w-3.5" /></span>
                  <Input value={gorgiasVal} onChange={(e) => setGorgiasVal(e.target.value)} autoFocus
                         onKeyDown={(e) => { if (e.key === 'Enter') saveGorgias(); if (e.key === 'Escape') setEditingGorgias(false); }}
                         placeholder="https://lamptwist.gorgias.com/app/ticket/…" className="h-8 min-w-[240px] flex-1 text-[12px]" />
                  <Button size="sm" onClick={saveGorgias} disabled={savingGorgias}>{savingGorgias ? <PetanqueLoader size={14} /> : <Check className="h-4 w-4" />} Save</Button>
                  <button type="button" onClick={() => setEditingGorgias(false)} className="text-[12px] text-muted-foreground hover:underline">Cancel</button>
                </div>
              ) : pf.gorgias_url ? (
                <FileRow icon={Link2} title={`Gorgias ticket${ticketNo(pf.gorgias_url) ? ` #${ticketNo(pf.gorgias_url)}` : ''}`} href={pf.gorgias_url}
                         subtitle={pf.vendor_notified_at
                           ? `Vendor notified ${fmtDate(pf.vendor_notified_at)}${pf.vendor_notified_via === 'gorgias' ? ' on the ticket' : ' by email'}`
                           : 'Vendor not told yet'}
                         actions={<>
                           {canModify && <Quiet onClick={() => { setGorgiasVal(pf.gorgias_url); setEditingGorgias(true); }} title="Change the ticket" className="px-2">Edit</Quiet>}
                           <Quiet onClick={() => window.open(pf.gorgias_url, '_blank', 'noopener')} title="Open the ticket"><ExternalLink className="h-3.5 w-3.5" /></Quiet>
                         </>} />
              ) : canModify && (
                <button type="button" onClick={() => { setGorgiasVal(''); setEditingGorgias(true); }}
                        className="px-2.5 py-1.5 text-[12px] text-muted-foreground hover:text-primary">+ Add Gorgias ticket</button>
              )}
              {docErr && <div className="px-2.5 text-[12px] text-rose-600">{docErr}</div>}
            </div>

            {/* extra info: only a quiet add link when empty */}
            <div className="mt-2 px-2.5">
              {editingRemarks ? (
                <div>
                  <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} autoFocus
                    placeholder="What it's for, payment terms… (URLs become clickable)"
                    className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary" />
                  <div className="mt-1.5 flex items-center gap-3">
                    <Button size="sm" onClick={saveRemarks}>Save</Button>
                    <button onClick={() => setEditingRemarks(false)} className="text-[12px] text-muted-foreground hover:underline">Cancel</button>
                  </div>
                </div>
              ) : pf.remarks ? (
                <div className="grid grid-cols-[80px_1fr] items-start gap-3 py-1.5">
                  <div className="text-[12px] text-muted-foreground">Extra info</div>
                  <div className="flex min-w-0 items-start gap-2">
                    <div className="min-w-0 flex-1 whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/80"><Linkify text={pf.remarks} /></div>
                    {canModify && <Quiet onClick={() => { setRemarks(pf.remarks || ''); setEditingRemarks(true); }} title="Edit"><Pencil className="h-3.5 w-3.5" /></Quiet>}
                  </div>
                </div>
              ) : canModify && (
                <button type="button" onClick={() => { setRemarks(''); setEditingRemarks(true); }}
                        className="py-1.5 text-[12px] text-muted-foreground hover:text-primary">+ Add extra info</button>
              )}
            </div>
          </div>

          {/* ------------------------------------------------ 3. activity */}
          <div className="border-t border-border px-5 pb-7 pt-5 sm:px-7">
            <h3 className="mb-4 px-2.5 text-[13px] font-semibold text-foreground/80">Activity</h3>
            <ol className="space-y-4 px-2.5">
              {activity.map((item) => (item.kind === 'status' ? (
                <li key={item.key} className="flex gap-3">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/12 text-[12px] font-semibold text-primary">{initial(item.h.actor_name)}</span>
                  <div className="min-w-0 flex-1 pt-1">
                    <div className="flex flex-wrap items-center gap-x-1.5 text-[13px] text-muted-foreground">
                      <span className="font-semibold text-foreground/80">{item.h.actor_name || 'Someone'}</span>
                      <span>{!item.h.from_status ? 'logged it as' : item.h.from_status === item.h.to_status ? 'noted on' : 'moved it to'}</span>
                      <Badge variant={(PROFORMA_STATUS[item.h.to_status] || {}).variant || 'default'} className="rounded-full px-2 py-0 text-[11px] font-semibold">
                        {(PROFORMA_STATUS[item.h.to_status] || {}).label || item.h.to_status}
                      </Badge>
                      <Dot /><span>{timeAgo(item.h.created_at)}</span>
                    </div>
                    {item.h.note && item.h.note !== 'Logged' && <div className="mt-0.5 text-[13px] leading-normal text-foreground/80">{item.h.note}</div>}
                  </div>
                </li>
              ) : (
                <li key={item.key} className="flex gap-3">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/12 text-[12px] font-semibold text-primary">{initial(item.c.author_name)}</span>
                  <div className="min-w-0 flex-1 pt-1">
                    <div className="flex items-center gap-x-1.5 text-[13px] text-muted-foreground">
                      <span className="font-semibold text-foreground/80">{item.c.author_name || 'Someone'}</span>
                      <Dot /><span>{timeAgo(item.c.created_at)}{item.c.edited_at ? ' · edited' : ''}</span>
                      {user?.id && item.c.author_id === user.id && editingId !== item.c.id && (
                        <span className="ml-auto flex items-center">
                          <Quiet onClick={() => startEdit(item.c)} title="Edit note"><Pencil className="h-3.5 w-3.5" /></Quiet>
                          <Quiet onClick={() => removeNote(item.c)} title="Delete note" className="hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Quiet>
                        </span>
                      )}
                    </div>
                    {editingId === item.c.id ? (
                      <div className="mt-1">
                        <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={2}
                                  className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary" />
                        {(item.c.attachments?.some((a) => !editRemove.includes(a.id)) || editFiles.length > 0) && (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            {item.c.attachments?.filter((a) => !editRemove.includes(a.id)).map((a) => (
                              <span key={a.id} className="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-[12px]">
                                <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">{a.name}</span>
                                <button type="button" onClick={() => setEditRemove((prev) => [...prev, a.id])} title="Remove" className="shrink-0 text-muted-foreground hover:text-rose-600"><X className="h-3 w-3" /></button>
                              </span>
                            ))}
                            {editFiles.map((f, i) => (
                              <span key={`${f.name}-${i}`} className="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-primary/50 bg-primary/8 px-2 py-1 text-[12px]">
                                <Paperclip className="h-3.5 w-3.5 shrink-0 text-primary" /><span className="truncate">{f.name}</span>
                                <button type="button" onClick={() => setEditFiles((prev) => prev.filter((_, j) => j !== i))} className="shrink-0 text-muted-foreground hover:text-rose-600"><X className="h-3 w-3" /></button>
                              </span>
                            ))}
                          </div>
                        )}
                        <input ref={editFileRef} type="file" multiple hidden accept={ACCEPT}
                               onChange={(e) => { const ok = validFiles(Array.from(e.target.files || []), setAttachErr); if (ok) setEditFiles((p) => [...p, ...ok]); if (editFileRef.current) editFileRef.current.value = ''; }} />
                        <div className="mt-1.5 flex items-center gap-2">
                          <Button size="sm" onClick={() => saveEdit(item.c)}>Save</Button>
                          <button onClick={cancelEdit} className="text-[12px] text-muted-foreground hover:underline">Cancel</button>
                          <Quiet onClick={() => editFileRef.current?.click()} className="ml-auto"><Paperclip className="h-3.5 w-3.5" /> Attach</Quiet>
                        </div>
                      </div>
                    ) : (
                      item.c.body && <div className="mt-0.5 whitespace-pre-wrap text-[13px] leading-normal text-foreground/80"><Linkify text={item.c.body} /></div>
                    )}
                    {item.c.attachments?.length > 0 && editingId !== item.c.id && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {item.c.attachments.map((a) => (
                          <button key={a.id} type="button" onClick={() => openFile(a)} title={a.name}
                                  className="inline-flex max-w-[220px] items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-[12px] hover:bg-muted">
                            <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">{a.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {item.c.mentions?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {item.c.mentions.map((m) => <Badge key={m.id} variant="accent" className="rounded-full">@{m.name}</Badge>)}
                      </div>
                    )}
                  </div>
                </li>
              )))}

              {/* composer: one line until focused */}
              <li className="flex gap-3">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-muted text-[12px] font-semibold text-muted-foreground">{initial(user?.displayName || user?.first_name)}</span>
                <div className="min-w-0 flex-1 rounded-[10px] border border-border bg-card focus-within:border-primary">
                  <MentionTextarea rows={composerShown ? 3 : 1} placeholder="Add a note – type @ to mention a colleague…"
                                   value={body} onChange={setBody} users={users}
                                   onFocus={() => setComposerOpen(true)}
                                   textareaClassName="min-h-0 resize-none border-0 bg-transparent px-3 py-2.5 text-[14px] ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                                   onMention={(u) => setMentions((prev) => (prev.some((m) => m.id === u.id) ? prev : [...prev, u]))} />
                  {composerShown && (
                    <div className="flex flex-wrap items-center gap-1.5 border-t border-border/60 px-2 py-1.5 pl-3">
                      {mentions.map((m) => (
                        <Badge key={m.id} variant="accent" className="cursor-pointer rounded-full" onClick={() => setMentions((prev) => prev.filter((x) => x.id !== m.id))}>
                          @{m.name} <X className="h-3 w-3" />
                        </Badge>
                      ))}
                      {files.map((f, i) => (
                        <span key={`${f.name}-${i}`} className="inline-flex max-w-[220px] items-center gap-1.5 rounded-md border border-border bg-card px-2 py-0.5 text-[12px]">
                          <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">{f.name}</span>
                          <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} className="shrink-0 text-muted-foreground hover:text-rose-600"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                      {attachErr && <span className="text-[12px] text-rose-600">{attachErr}</span>}
                      <input ref={fileRef} type="file" multiple hidden accept={ACCEPT}
                             onChange={(e) => { const ok = validFiles(Array.from(e.target.files || []), setAttachErr); if (ok) setFiles((p) => [...p, ...ok]); if (fileRef.current) fileRef.current.value = ''; }} />
                      <Quiet onClick={() => fileRef.current?.click()} title="Attach images or PDFs (max 10 MB each)" className={cn(mentions.length === 0 && files.length === 0 ? '' : 'ml-1')}>
                        <Paperclip className="h-3.5 w-3.5" /> Attach
                      </Quiet>
                      <Button size="sm" className="ml-auto h-8 rounded-lg px-3.5 text-[13px] font-semibold" onClick={postComment}
                              disabled={posting || (!body.trim() && files.length === 0)}>
                        {posting ? <PetanqueLoader size={16} /> : <Send className="h-4 w-4" />} Post
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            </ol>
          </div>

          <PaymentSheet proformaId={pf.id} open={paySheet} onClose={() => setPaySheet(false)}
                        onPaid={async () => { setPaySheet(false); await refresh(); }} />
        </div>
      )}
    </Dialog>
  );
}
